/**
 * DocListCenter - Center-panel list view for documents
 *
 * Shown when no document is selected in DocumentationReader.
 * Provides search, type filter, and a table of documents.
 */

import { useState, useMemo } from 'react';
import {
  FileText,
  ClipboardCheck,
  FileCode,
  FlaskConical,
  Plus,
  Loader2,
  ChevronRight,
  FolderOpen,
} from 'lucide-react';
import type { EditorDocument } from '../../api/documents';
import type { DocumentCategory } from '../shared/ReaderSidebar.adapters';

// =============================================================================
// Props
// =============================================================================

interface DocListCenterProps {
  documents: EditorDocument[];
  isLoading: boolean;
  onDocumentSelect: (doc: EditorDocument) => void;
  onCreateDocument: (type: DocumentCategory) => void;
}

// =============================================================================
// Helpers
// =============================================================================

type DocTypeFilter = 'all' | 'requirements' | 'specifications' | 'tests' | 'general';

function getDocCategory(doc: EditorDocument): DocTypeFilter {
  if (doc.tags?.includes('requirements')) return 'requirements';
  if (doc.doc_type === 'spec') return 'specifications';
  if (doc.doc_type === 'test') return 'tests';
  return 'general';
}

function getDocTypeIcon(doc: EditorDocument) {
  if (doc.tags?.includes('requirements')) return <ClipboardCheck size={14} className="text-coral-500" />;
  if (doc.doc_type === 'spec') return <FileCode size={14} className="text-indigo-500" />;
  if (doc.doc_type === 'test') return <FlaskConical size={14} className="text-emerald-500" />;
  return <FileText size={14} className="text-neutral-400" />;
}

function getDocTypeBadge(doc: EditorDocument) {
  if (doc.tags?.includes('requirements')) {
    return { label: 'Requirements', className: 'bg-coral-50 text-coral-700' };
  }
  if (doc.doc_type === 'spec') {
    return { label: 'Specification', className: 'bg-indigo-50 text-indigo-700' };
  }
  if (doc.doc_type === 'test') {
    return { label: 'Test Plan', className: 'bg-emerald-50 text-emerald-700' };
  }
  return { label: 'Document', className: 'bg-neutral-100 text-neutral-600' };
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// DocListCenter
// =============================================================================

export function DocListCenter({
  documents,
  isLoading,
  onDocumentSelect,
  onCreateDocument,
}: DocListCenterProps) {
  const [typeFilter, setTypeFilter] = useState<DocTypeFilter>('all');

  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      // Exclude content-type docs (those belong to content service)
      if (doc.doc_type === 'content') return false;

      if (typeFilter !== 'all' && getDocCategory(doc) !== typeFilter) return false;

      return true;
    });
  }, [documents, typeFilter]);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-6 border-b border-neutral-100 bg-neutral-50/50 shrink-0">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-neutral-800">Documents</h2>
          <span className="text-xs text-neutral-400">
            {documents.filter((d) => d.doc_type !== 'content').length} document{documents.filter((d) => d.doc_type !== 'content').length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-3 shrink-0">
        {/* Type filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as DocTypeFilter)}
          className="px-3 py-2 text-sm bg-white border border-neutral-200 rounded-lg
            focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
        >
          <option value="all">All Types</option>
          <option value="requirements">Requirements</option>
          <option value="specifications">Specifications</option>
          <option value="tests">Tests</option>
          <option value="general">General</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {isLoading ? (
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3.5 border-b border-neutral-100 animate-pulse">
                <div className="w-8 h-8 rounded-lg bg-neutral-100" />
                <div className="h-4 w-48 bg-neutral-200 rounded" />
                <div className="h-4 w-20 bg-neutral-100 rounded" />
                <div className="h-4 w-24 bg-neutral-100 rounded" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex-1 flex items-center justify-center min-h-[400px]">
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-16 text-center max-w-lg w-full">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <FolderOpen className="w-8 h-8 text-neutral-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {typeFilter !== 'all' ? 'No matching documents' : 'No documents yet'}
              </h3>
              <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
                {typeFilter !== 'all'
                  ? 'Try adjusting your filter criteria.'
                  : 'Create your first document to get started.'}
              </p>
              {typeFilter === 'all' && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => onCreateDocument('general')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-coral-500 text-white rounded-xl
                      font-medium text-sm hover:bg-coral-600 transition-colors shadow-sm"
                  >
                    <Plus size={14} />
                    New Document
                  </button>
                  <button
                    onClick={() => onCreateDocument('requirements')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-neutral-700 border border-neutral-200 rounded-xl
                      font-medium text-sm hover:bg-neutral-50 transition-colors"
                  >
                    <ClipboardCheck size={14} />
                    New Requirements
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              <div className="col-span-5">Title</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Tags</div>
              <div className="col-span-3">Updated</div>
            </div>

            {/* Rows */}
            {filtered.map((doc) => {
              const badge = getDocTypeBadge(doc);
              return (
                <button
                  key={doc.id}
                  onClick={() => onDocumentSelect(doc)}
                  className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neutral-100
                    hover:bg-neutral-50 transition-colors w-full text-left group"
                >
                  <div className="col-span-5 flex items-center gap-2 min-w-0">
                    {getDocTypeIcon(doc)}
                    <span className="text-sm font-medium text-neutral-800 truncate group-hover:text-coral-600 transition-colors">
                      {doc.title}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center gap-1 min-w-0">
                    {doc.tags?.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded truncate max-w-[80px]">
                        {tag}
                      </span>
                    ))}
                    {(doc.tags?.length || 0) > 2 && (
                      <span className="text-[10px] text-neutral-400">+{doc.tags!.length - 2}</span>
                    )}
                  </div>
                  <div className="col-span-3 text-xs text-neutral-500 flex items-center justify-between">
                    <span>{formatDate(doc.updated_at)}</span>
                    <ChevronRight size={14} className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
